import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  LogIn,
  LogOut,
  Clock,
  Monitor,
  Globe,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
  User,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/services/api";
import { toast } from "sonner";

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active: {
    label: "Active",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  logged_out: {
    label: "Logged Out",
    className: "bg-slate-50 text-slate-600 border-slate-200",
  },
  session_expired: {
    label: "Expired",
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
};

const ROLE_COLORS = {
  admin: "bg-indigo-50 text-indigo-700 border-indigo-200",
  reviewer: "bg-slate-50 text-slate-600 border-slate-200",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function parseUA(ua) {
  if (!ua) return "Unknown";
  if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) return "Chrome";
  if (/Firefox/i.test(ua)) return "Firefox";
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return "Safari";
  if (/Edg/i.test(ua)) return "Edge";
  if (/MSIE|Trident/i.test(ua)) return "IE";
  return "Browser";
}

// ── component ─────────────────────────────────────────────────────────────────

export const ActivityLogPage = () => {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");

  const PAGE_SIZE = 50;

  const fetchUsers = useCallback(async () => {
    try {
      const data = await api.users.list();
      setUsers(data);
    } catch {
      // non-critical
    }
  }, []);

  const fetchLog = useCallback(
    async (currentPage = 1, showRefreshing = false) => {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);
      try {
        const data = await api.activityLog.list({
          page: currentPage,
          pageSize: PAGE_SIZE,
          userId: userFilter !== "all" ? userFilter : null,
          status: statusFilter !== "all" ? statusFilter : null,
        });
        setSessions(data.sessions || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      } catch (err) {
        toast.error("Failed to load activity log");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [userFilter, statusFilter]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setPage(1);
    fetchLog(1);
  }, [userFilter, statusFilter, fetchLog]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchLog(newPage, true);
  };

  // ── KPI counts from current data ──────────────────────────────────────────
  const activeCount = sessions.filter((s) => s.status === "active").length;
  const loggedOutCount = sessions.filter((s) => s.status === "logged_out").length;
  const expiredCount = sessions.filter((s) => s.status === "session_expired").length;
  const avgDuration =
    sessions.filter((s) => s.duration_minutes != null).length > 0
      ? sessions
          .filter((s) => s.duration_minutes != null)
          .reduce((sum, s) => sum + s.duration_minutes, 0) /
        sessions.filter((s) => s.duration_minutes != null).length
      : null;

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Activity Log
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Login sessions for all users — {total} total records
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchLog(page, true)}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              icon: Activity,
              label: "Total Sessions",
              value: total,
              color: "bg-indigo-100 text-indigo-950",
            },
            {
              icon: LogIn,
              label: "Active Now",
              value: activeCount,
              color: "bg-emerald-100 text-emerald-700",
            },
            {
              icon: LogOut,
              label: "Logged Out",
              value: loggedOutCount,
              color: "bg-slate-100 text-slate-600",
            },
            {
              icon: Clock,
              label: "Avg Duration",
              value: formatDuration(avgDuration),
              color: "bg-amber-100 text-amber-700",
            },
          ].map(({ icon: Icon, label, value, color }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3"
            >
              <div className={`p-2.5 rounded-lg ${color}`}>
                <Icon className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs text-slate-500">{label}</p>
                <p
                  className="text-xl font-bold text-slate-900"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {value}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
          <Filter className="h-4 w-4 text-slate-400 shrink-0" />
          <span className="text-sm text-slate-500 font-medium">Filter:</span>

          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="logged_out">Logged Out</SelectItem>
              <SelectItem value="session_expired">Expired</SelectItem>
            </SelectContent>
          </Select>

          {(userFilter !== "all" || statusFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setUserFilter("all");
                setStatusFilter("all");
              }}
              className="text-slate-400 hover:text-slate-600"
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-slate-400">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              Loading sessions...
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400 gap-2">
              <Activity className="h-8 w-8" strokeWidth={1} />
              <p className="text-sm">No sessions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Login Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">
                      Logout Time
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                      IP Address
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">
                      Browser
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sessions.map((s, idx) => {
                    const statusCfg =
                      STATUS_CONFIG[s.status] || STATUS_CONFIG.session_expired;
                    return (
                      <motion.tr
                        key={s.session_id || idx}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.02 }}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        {/* User */}
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <User className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">
                                {s.full_name}
                              </p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <p className="text-xs text-slate-400 truncate">
                                  {s.email}
                                </p>
                                <Badge
                                  variant="outline"
                                  className={`text-xs shrink-0 ${ROLE_COLORS[s.role] || ROLE_COLORS.reviewer}`}
                                >
                                  {s.role}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <Badge
                            variant="outline"
                            className={`text-xs ${statusCfg.className}`}
                          >
                            {s.status === "active" && (
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse mr-1.5 inline-block" />
                            )}
                            {statusCfg.label}
                          </Badge>
                        </td>

                        {/* Login time */}
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <LogIn className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                            {formatDateTime(s.login_at)}
                          </div>
                        </td>

                        {/* Logout time */}
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap hidden md:table-cell">
                          {s.logout_at ? (
                            <div className="flex items-center gap-1.5">
                              <LogOut className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              {formatDateTime(s.logout_at)}
                            </div>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>

                        {/* Duration */}
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`text-sm font-semibold ${
                              s.duration_minutes != null
                                ? "text-slate-700"
                                : "text-slate-300"
                            }`}
                            style={{ fontFamily: "Manrope, sans-serif" }}
                          >
                            {formatDuration(s.duration_minutes)}
                          </span>
                        </td>

                        {/* IP */}
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Globe className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                            {s.ip_address || "—"}
                          </div>
                        </td>

                        {/* Browser */}
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Monitor className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                            {parseUA(s.user_agent)}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {page} of {pages} &mdash; {total} sessions
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || refreshing}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= pages || refreshing}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
